// components/ProductHistoryModal.jsx
import { useEffect, useState } from "react";
import { Modal, Button, Table, Badge, Spinner } from "react-bootstrap";
import api from "../../../api";
import { formatDateOnly as formatCalendarDay } from "../../../utils/dateOnly";
import { lifecycleLabel } from "../../../utils/assetLifecycle";

const DISCARD_REASON_LABELS = {
  END_OF_USEFUL_LIFE: "End of Useful Life",
  DAMAGED: "Damaged",
  BEYOND_REPAIR: "Beyond Repair",
  OBSOLETE: "Obsolete",
  UNSERVICEABLE: "Unserviceable",
  LOST: "Lost",
  OTHER: "Other",
};

// Calendar day, never shifted by the browser's timezone (utils/dateOnly.js).
function formatDateOnly(value) {
  return formatCalendarDay(value, "-");
}

export default function ProductHistoryModal({ product, show, onHide }) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!show || !product?.id) return;

    setLoading(true);

    api
      .get(`/products/${product.id}/allocations?limit=100`)
      .then(({ data }) => setPayload(data))
      .catch((error) =>
        setPayload({
          error: error?.response?.data?.error || error.message,
        })
      )
      .finally(() => setLoading(false));
  }, [show, product?.id]);

  const getStatusBadge = (status) => {
    if (status === "ALLOCATED") {
      return <Badge className="unit-status">Allocated</Badge>;
    }

    if (status === "RETURNED") {
      return <Badge className="unit-status">Returned</Badge>;
    }

    if (status === "CANCELLED") {
      return <Badge className="unit-status">Cancelled</Badge>;
    }

    return <Badge className="unit-status">{status || "-"}</Badge>;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      className="unit-modal product-history-modal"
      dialogClassName="admin-teal-modal admin-teal-modal-xl"
      backdropClassName="admin-teal-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Allocation History
          {product?.name ? ` — ${product.name}` : ""}
          {product?.sku ? ` (${product.sku})` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="unit-loader">
            <Spinner animation="border" />
            <p>Loading allocation history...</p>
          </div>
        )}

        {!loading && payload?.error && (
          <div className="unit-empty">
            <div className="unit-empty-icon">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
            </div>
            <h5>Unable to load history</h5>
            <p className="text-danger">{payload.error}</p>
          </div>
        )}

        {!loading && payload && !payload.error && (
          <>
            <div className="unit-stats-row mb-4">
              <div className="unit-stat-card">
                <span>Total Allocated</span>
                <strong>{payload.summary?.total_allocated || 0}</strong>
              </div>

              <div className="unit-stat-card">
                <span>Total Returned</span>
                <strong>{payload.summary?.total_returned || 0}</strong>
              </div>

              <div className="unit-stat-card">
                <span>Currently Out</span>
                <strong>{payload.summary?.currently_out || 0}</strong>
              </div>

              <div className="unit-stat-card">
                <span>Last Status</span>
                <strong style={{ fontSize: "18px" }}>
                  {payload.summary?.last_status || "-"}
                </strong>
              </div>
            </div>

            {payload.summary?.last_user_name && (
              <div className="unit-card mb-3" style={{ boxShadow: "none" }}>
                <div className="p-3">
                  <p className="mb-1 text-muted">Last Allocation Activity</p>
                  <div className="unit-name-box">
                    <div className="unit-letter">
                      {payload.summary.last_user_name
                        ?.charAt(0)
                        ?.toUpperCase() || "U"}
                    </div>

                    <div>
                      <strong>{payload.summary.last_user_name}</strong>
                      <small>
                        {getStatusBadge(payload.summary.last_status)}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {payload.product && (
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="unit-card h-100" style={{ boxShadow: "none" }}>
                    <div className="p-3">
                      <p className="mb-2 text-muted fw-bold">PRODUCT LIFE</p>
                      <div className="small">
                        Purchase Date: {formatDateOnly(payload.product.purchase_date)}
                      </div>
                      <div className="small">
                        Asset Life: {payload.product.useful_life_label || "—"}
                      </div>
                      <div className="small">
                        End Life Date: {formatDateOnly(payload.product.expected_end_date)}
                        {payload.product.expected_life_override_end_date ? " (revised by Life Review)" : ""}
                      </div>
                      <div className="small">
                        Remaining / Exceeded: {payload.product.remaining_life_label || "-"}
                      </div>
                      <div className="mt-2">
                        <Badge className="unit-status">
                          {lifecycleLabel(payload.product.lifecycle)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="unit-card h-100" style={{ boxShadow: "none" }}>
                    <div className="p-3">
                      <p className="mb-2 text-muted fw-bold">OPERATIONAL STATUS</p>
                      {/* Discarded (permanent retirement) must never look the
                          same as Inactive (reversible) -- same distinct
                          classes as the Product List's own status badge. */}
                      <Badge
                        className={`unit-status ${
                          payload.product.is_discarded
                            ? "unit-status-discarded"
                            : payload.product.status === "INACTIVE"
                            ? "unit-status-inactive"
                            : "unit-status-active"
                        }`}
                      >
                        {payload.product.is_discarded
                          ? "Discarded"
                          : payload.product.status === "INACTIVE"
                          ? "Inactive"
                          : "Active"}
                      </Badge>
                    </div>
                  </div>
                </div>

                {payload.product.is_discarded && (
                  <div className="col-md-4">
                    <div className="unit-card h-100" style={{ boxShadow: "none" }}>
                      <div className="p-3">
                        <p className="mb-2 text-muted fw-bold">DISCARD DETAILS</p>
                        <div className="small">Discard Date: {formatDateOnly(payload.product.discarded_at)}</div>
                        <div className="small">Discarded By: {payload.product.discarded_by_name || "-"}</div>
                        <div className="small">
                          Reason: {DISCARD_REASON_LABELS[payload.product.discard_reason] || payload.product.discard_reason || "-"}
                        </div>
                        {payload.product.discard_remarks && (
                          <div className="small">Remarks: {payload.product.discard_remarks}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="unit-table-wrapper p-0">
              <Table responsive className="unit-table">
                <thead>
                  <tr>
                    <th style={{ width: "80px" }}>S.No.</th>
                    <th>Status</th>
                    <th>Qty</th>
                    <th>User</th>
                    <th>Unit</th>
                    <th>Allocated At</th>
                    <th>Returned At</th>
                    <th>Updated</th>
                    <th>Notes</th>
                  </tr>
                </thead>

                <tbody>
                  {payload.items?.length > 0 ? (
                    payload.items.map((row, index) => (
                      <tr key={row.id}>
                        <td>
                          <span className="unit-id">#{index + 1}</span>
                        </td>

                        <td>{getStatusBadge(row.status)}</td>

                        <td>
                          <span className="unit-id">{row.qty || 0}</span>
                        </td>

                        <td>
                          <div className="unit-name-box">
                            <div className="unit-letter">
                              {(row.user_name || row.user_id || "U")
                                .toString()
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <strong>{row.user_name || row.user_id || "-"}</strong>
                              <small>User</small>
                            </div>
                          </div>
                        </td>

                        <td>
                          <Badge className="unit-status">
                            {row.unit_name || row.unit_id || "-"}
                          </Badge>
                        </td>

                        <td>{row.allocated_at || "-"}</td>
                        <td>{row.return_at || "-"}</td>
                        <td>{row.updated_at || "-"}</td>

                        <td
                          style={{
                            maxWidth: "220px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={row.notes || ""}
                        >
                          {row.notes || "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="unit-empty">
                          <div className="unit-empty-icon">
                            <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
                          </div>
                          <h5>No history yet</h5>
                          <p>This product has no allocation history.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button className="unit-btn unit-btn-light" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}